import moment from "moment";
import { useContext, useEffect, useMemo, useState } from "react";
import { InstrumentTypeResponse } from "../../../codegen-api";
import { WebsocketContext } from "../../../contexts/WebsocketAuthContext";
import { nanosToSeconds } from "../../../utils/date";
import { getExpiryFromSymbol } from "../../../utils/instruments";
import { forEachFrame, readFrame } from "../parseFrame";
import { useGetOptionsChain } from "../../api/optionsChain/useGetOptionsChain";
import { WebsocketChannelEnum } from "../model/shared";
import { IWSSTicker, IWSSTickerResponse } from "../model/ticker";
import { AssetResponse } from "../../../utils/asset";
import { displayFromRaw18 } from "../../../utils/displayDecimals";
import { knownDisplayPrice } from "../../../utils/markGuards";

export type InstrumentToTicker = {
  [key: string]: IWSSTicker;
};

export const getTickerChannel = (
  asset: AssetResponse,
  derivative: InstrumentTypeResponse
) => `${WebsocketChannelEnum.TICKER}:${asset}:${derivative}`;

const useTickersWSS = (
  asset?: AssetResponse,
  derivative?: InstrumentTypeResponse,
  expiry?: number
) => {
  const { triggerSubscribe, triggerUnsubscribe, lastMessages } =
    useContext(WebsocketContext);
  const { data: optionsChainData } = useGetOptionsChain(asset, expiry);

  const [instrumentTicker, setInstrumentTicker] = useState<InstrumentToTicker>(
    {}
  );

  useEffect(() => {
    if (!asset || !derivative) {
      return undefined;
    }

    const data = [getTickerChannel(asset, derivative)];
    triggerSubscribe("subscribe", data);
    return () => triggerUnsubscribe(data);
  }, [asset, derivative, triggerSubscribe, triggerUnsubscribe]);

  // Receives messages and updates state
  useEffect(() => {
    if (!lastMessages || !asset || !derivative) return;

    // Parsed once per frame, outside any state updater (FE-12), and applied
    // in one update per batch.
    const tickerChannel = getTickerChannel(asset, derivative);
    const isPerp = derivative === InstrumentTypeResponse.Perpetual;
    let tickers: InstrumentToTicker | undefined;
    forEachFrame(lastMessages, "useTickersWSS", (lastMessage) => {
      const frame = readFrame<IWSSTickerResponse>(lastMessage);
      if (!frame) return;
      const { data, channel } = frame;
      if (channel !== tickerChannel || !data?.tickers?.length) return;

      // The live WS feed forwards raw 18-dec on-chain values. For perps,
      // normalize price/OI fields to display units so they match the REST
      // path (guarded so already-scaled values are never double-divided).
      // Options/spot are left untouched. See utils/displayDecimals.
      data.tickers.forEach((raw) => {
        const curr: any = raw;
        const norm = isPerp
          ? {
              ...curr,
              // An omitted (or zero) price stays undefined, never "0".
              index_price: knownDisplayPrice(curr.index_price),
              mark_price: knownDisplayPrice(curr.mark_price),
              open_interest: displayFromRaw18(curr.open_interest),
              ...(curr.mark
                ? {
                    mark: {
                      ...curr.mark,
                      price: knownDisplayPrice(curr.mark.price),
                    },
                  }
                : {}),
            }
          : curr;
        tickers = { ...(tickers || {}), [curr.instrument_name]: norm };
      });
    });

    if (tickers) {
      const next = tickers;
      setInstrumentTicker((prev) => ({ ...prev, ...next }));
    }
  }, [asset, derivative, lastMessages]);

  const filteredInstrumentTicker = useMemo(() => {
    const filteredTickers = Object.keys(instrumentTicker)
      .filter((k) => {
        if (expiry) {
          const instrumentExp = getExpiryFromSymbol(
            instrumentTicker[k].instrument_name
          );
          const exp = moment.unix(nanosToSeconds(expiry)).format("DD-MM-YYYY");
          return exp === instrumentExp;
        }
        return true;
      })
      .reduce(
        (prev, current) => ({
          ...prev,
          [current]: instrumentTicker[current],
        }),
        {} as InstrumentToTicker
      );

    // Transform options chain obj to instrumentTicker
    // Combine options chain data with filtered tickers
    if (optionsChainData && optionsChainData.asset === asset) {
      [
        ...(optionsChainData.options?.calls || []),
        ...(optionsChainData.options?.puts || []),
      ].forEach((opt) => {
        if (opt.expiry === String(expiry)) {
          filteredTickers[opt.instrument_name] = filteredTickers[
            opt.instrument_name
          ] || {
            instrument_id: opt.instrument_id,
            instrument_name: opt.instrument_name,
            open_interest: opt.open_interest,
            ask: {
              price: opt.ticker?.ask?.price,
              amount: opt.ticker?.ask?.volume,
              greeks: {
                iv: opt.ticker?.ask?.iv,
              },
            },
            bid: {
              price: opt.ticker?.bid?.price,
              amount: opt.ticker?.bid?.volume,
              greeks: {
                iv: opt.ticker?.bid?.iv,
              },
            },
            mark: {
              greeks: {
                delta: opt.delta,
                iv: opt.iv,
              },
              price: opt.mark_price,
            },
          };
        }
      });
      return filteredTickers;
    }
    return filteredTickers;
  }, [asset, expiry, instrumentTicker, optionsChainData]);

  return {
    instrumentTicker: filteredInstrumentTicker,
  };
};

export default useTickersWSS;
